import { toApiError } from '@/shared/api';

const NO_ANSWER = new Set([0, 502, 503, 504]);

export type PasswordOutcome =
  | { kind: 'expired' }
  | { kind: 'activated' }
  | { kind: 'card'; title: string; text: string };

/**
 * Was nach einem gescheiterten Speichern des Passworts zu sagen ist. Der Server prüft den Link erst
 * beim Speichern; ein abgelehnter Link bekommt deshalb danach eine eigene Seite. Ohne Antwort ist
 * offen, ob das Passwort gespeichert wurde.
 */
export async function passwordFailure(error: unknown): Promise<PasswordOutcome> {
  const problem = await toApiError(error);
  if (
    problem.title === 'Auth.InvalidResetToken' ||
    problem.title === 'Auth.InvalidConfirmationToken'
  )
    return { kind: 'expired' };
  if (problem.title === 'Auth.AccountAlreadyActivated') return { kind: 'activated' };
  if (NO_ANSWER.has(problem.status))
    return {
      kind: 'card',
      title: 'Keine Antwort vom Server.',
      text: 'Offen ist, ob das Passwort gespeichert wurde. Versuch die Anmeldung mit dem neuen Passwort.',
    };
  if (problem.status === 429)
    return {
      kind: 'card',
      title: 'Zu viele Versuche.',
      text: 'Warte einen Moment und versuch es dann noch einmal.',
    };
  if (problem.title === 'Auth.WeakPassword')
    return {
      kind: 'card',
      title: 'Passwort abgelehnt.',
      text: 'Der Server verlangt ein stärkeres Passwort. Prüf die Regeln unter dem Feld.',
    };
  // Ein Link mit kaputter Kennung oder Token scheitert schon an der Prüfung der Eingaben.
  const fields = Object.keys(problem.fieldErrors ?? {}).map((key) => key.toLowerCase());
  if (problem.status === 400 && fields.some((key) => key === 'userid' || key === 'token'))
    return { kind: 'expired' };
  if (problem.status === 400)
    return {
      kind: 'card',
      title: 'Eingabe abgelehnt.',
      text: 'Der Server hat das Passwort nicht angenommen. Prüf die Regeln unter dem Feld.',
    };
  return {
    kind: 'card',
    title: 'Das hat nicht geklappt.',
    text: `Der Server meldet einen Fehler (Status ${problem.status}). Versuch es gleich noch einmal.`,
  };
}
